import { RFC3339timestamp } from "@bitory-gmbh/nidavellir";
import {
  BasePatch,
  DocumentBase,
  patchDocumentBase,
} from "@/services/backend/documents/types/base";
import {
  OfferReferenceBase,
  OfferReferenceBasePatch,
  patchOfferReferenceBase,
} from "@/services/backend/documents/types/offer-reference-base";
import { Information } from "@/services/backend/billing/types/information";
import { PaymentTerms } from "@/services/backend/billing/types/payment-terms";
import { ScopeOfWork } from "@/services/backend/omni/scope-of-work/types/scope-of-work";
import {
  patchPricing,
  patchPricingBy,
  Pricing,
  PricingBy,
  PricingByPatch,
  PricingPatch,
} from "@/services/backend/omni/pricing/types/pricing";

/** Mirrors `contract.State`, backend/core/omni/domain/contract/state.go. */
export enum ContractState {
  Draft = "",
  Signed = "signed",
}

/** Mirrors `contract.Contract`, backend/core/omni/domain/contract/contract.go. */
export type Contract = DocumentBase &
  OfferReferenceBase & {
    title: string;
    subtitle: string;
    text: string;
    specialAgreements: string;
    contractState: ContractState;
    startDate?: RFC3339timestamp;
    endDate?: RFC3339timestamp;
    scopeOfWork: ScopeOfWork[];
    pricing: Pricing;
    pricingBy: PricingBy;
    paymentTerms: PaymentTerms;
    billingInformation: Information;
  };

/** Mirrors `contract.Patch`, backend/core/omni/domain/contract/contract.go. */
export type Patch = BasePatch &
  OfferReferenceBasePatch & {
    title?: string;
    subtitle?: string;
    text?: string;
    specialAgreements?: string;
    contractState?: ContractState;
    startDate?: RFC3339timestamp | null;
    endDate?: RFC3339timestamp | null;
    pricing?: PricingPatch;
    pricingBy?: PricingByPatch;
    paymentTerms?: PaymentTerms;
    billingInformation?: Information;
  };

/**
 * patchContract returns the contract with the fields set in patch applied,
 * e.g. for an optimistic cache update. A date set to null is cleared.
 */
export function patchContract(contract: Contract, patch: Patch): Contract {
  let patched = patchDocumentBase(contract, patch);
  patched = patchOfferReferenceBase(patched, patch);

  return {
    ...patched,
    title: patch.title ?? patched.title,
    subtitle: patch.subtitle ?? patched.subtitle,
    text: patch.text ?? patched.text,
    specialAgreements: patch.specialAgreements ?? patched.specialAgreements,
    contractState: patch.contractState ?? patched.contractState,
    startDate:
      patch.startDate === undefined
        ? patched.startDate
        : (patch.startDate ?? undefined),
    endDate:
      patch.endDate === undefined
        ? patched.endDate
        : (patch.endDate ?? undefined),
    pricing: patchPricing(patched.pricing, patch.pricing),
    pricingBy: patchPricingBy(patched.pricingBy, patch.pricingBy),
    paymentTerms: patch.paymentTerms ?? patched.paymentTerms,
    billingInformation: patch.billingInformation ?? patched.billingInformation,
  };
}

/**
 * patchScopeOfWorkOfContract returns the contract with fn applied to its
 * scope of work scopeOfWorkId, e.g. for an optimistic cache update. Other
 * scope of works are returned as they are.
 */
export function patchScopeOfWorkOfContract(
  contract: Contract,
  scopeOfWorkId: string,
  fn: (scopeOfWork: ScopeOfWork) => ScopeOfWork,
): Contract {
  return {
    ...contract,
    scopeOfWork: contract.scopeOfWork.map((sow: ScopeOfWork) =>
      sow.id === scopeOfWorkId ? fn(sow) : sow,
    ),
  };
}
